import { BodyCell } from '@tableau/data-grid-1-wrapper';
import { SiteUrls } from '@tableau/shared-urls';
import { HorizontalOriginOption, TabStyles, TextLinkWidget, Tooltip, TooltipPosition, VerticalOriginOption } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { CellWithCountAndToolTip } from '~commonInfra/components/dataGrid/CellWithCount';
import { StyledCellFormatter } from '~commonInfra/components/dataGrid/StyledCellWrapper';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { Set } from '~commonInfra/utils/Set';
import { CellWithPopover } from '~src/commonInfra/components/dataGrid/CellWithPopover';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FileConnectionDetail } from '~src/datasource/dataConnection/serverApi/types/ConnectionDetail';
import { FlowOverviewDataGridRow } from '~src/flow/components/content/FlowOverviewGrid';
import { TooltipAnchor } from '~src/spacesPlatform/components/tooltip/TooltipAnchor';

import { Formatters, Messages } from '../codegen/Localize';
import { DataSources } from '../datasource/resources/Datasources';
import { FlowOutputStepCard } from '../flow/components/FlowOutputStepCard';
import { SiteSwitchService } from '../siteManagement/services/SiteSwitchService';
import { DownloadActions } from '../spacesPlatform/services/download/DownloadActions';
import { filePathDisplayStringWithoutBaseName } from '../spacesPlatform/utils/FilePathUtils';
import { baseBackgroundImgIcon, classes, cssClass, flexColumnStyle, flexRowStyle, textEllipsis } from '../style/index';
import { FlowSuspendedIconWithTooltip } from './components/FlowSuspendedIconWithTooltip';
import { DatasourceInfoForFlowOutput } from './models/DatasourceInfoForFlowOutput';
import { FlowBaseInfo } from './models/FlowInfo';
import { FlowOutputStepInfo } from './models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo, FlowOutputStepRunStatus } from './models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo, RunFlowTaskStatus } from './models/RunFlowTaskInfo';
import { FlowRunError } from './serverApi/types/FlowRunError';
import { FlowParameter, FlowParameterRun, FlowParameterType } from './types/FlowParameters';
import { connectionDetailsTypeToConnectionCategory,getConnectionCategory, getConnectionText, getOutputStepIconFromConnectionCategory } from './utils/FlowDataConnectionUtils';
import { openCreateFlowRunTaskDialog } from './utils/FlowDialogUtils';
import { getFlowOutputStepRunIconClass, getOutputIconStyle,getRunFlowTaskStatusIconClass, getStepIconClass } from './utils/FlowIconUtils';
import { areFlowParametersEnabled, isFlowParameterRun } from './utils/FlowParametersUtils';
import { flowRunErrorMessageWithLinks, isConnectionRelatedError, needsFixInPrepDesktop } from './utils/FlowRunErrorMessage';
import { localizeFlowRunHistoryStatus, localizeRunFlowTaskStatus } from './utils/FlowStatusUtils';

export module FlowDataGridCells {
  export function FlowOverviewOutputStepCell(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowOverviewOutputStepCell(props: BodyCell.Props) {
        const data = props.row.data;
        return <FlowOverviewOutputStepCellComponent outputName={data.outputName} outputStep={data.flowOutputStep}/>;
      }
    );
  }

  export function FlowOverviewOutputNameCell(siteUrls: SiteUrls): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowOverviewOutputNameCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowOverviewOutputNameCellComponent
            siteUrls={siteUrls}
            outputStep={data.flowOutputStep}
            outputStepMostRecentRun={data.outputStepMostRecentRun}
          />
        );
      }
    );
  }

  export function FlowDatasourcesForOutputCell(siteUrls: SiteUrls): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowDatasourcesForOutputCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowDatasourcesForOutputCellComponent
            siteUrls={siteUrls}
            outputStep={data.flowOutputStep}
            wasRanBefore={!!data.outputStepMostRecentRun}
          />
        );
      }
    );
  }

  export function FlowOverviewStatusCell(siteUrls: SiteUrls, schedulingFlowsEnabled: boolean): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowOverviewStatusCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowOverviewStatusCellComponent
            outputStep={data.flowOutputStep}
            outputStepMostRecentRun={data.outputStepMostRecentRun}
            flowId={data.runOutput.flowInfo.id}
            siteUrls={siteUrls}
            schedulingFlowsEnabled={schedulingFlowsEnabled}
          />
        );
      }
    );
  }

  export function FlowOverviewScheduleCell(siteUrls: SiteUrls, addScheduleAllowed: boolean, isServerOrSiteAdmin: boolean, flowFailureThresholdForRunPrevention: number, onUpdate: () => void): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowOverviewScheduleCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowOverviewScheduleCellComponent
            flow={data.runOutput.flowInfo}
            outputId={data.runOutput.outputId}
            tasks={data.schedules}
            siteUrls={siteUrls}
            addScheduleAllowed={addScheduleAllowed}
            isServerOrSiteAdmin={isServerOrSiteAdmin}
            onUpdate={onUpdate}
            flowFailureThresholdForRunPrevention={flowFailureThresholdForRunPrevention}
          />
        );
      }
    );
  }

  export function FlowOverviewErrorsCell(siteUrls: SiteUrls, isDownloadAllowed: boolean): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowOverviewErrorsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowOverviewErrorsCellComponent
            errors={data.outputStepMostRecentRun && data.outputStepMostRecentRun.flowRunErrors || null}
            flowId={data.runOutput.flowInfo.id}
            flowLuid={data.runOutput.flowInfo.luid}
            flowStatus={data.outputStepMostRecentRun && data.outputStepMostRecentRun.status}
            onConnectionsPage={false}
            siteUrls={siteUrls}
            isDownloadAllowed={isDownloadAllowed}
          />
        );
      }
    );
  }

  export function FlowRunHistoryErrorsCell(siteUrls: SiteUrls, isDownloadAllowed: boolean): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowRunHistoryErrorsCell(props: BodyCell.Props) {
        const data = (props.row.data as RunFlowTaskInfo);
        return (
          <FlowOverviewErrorsCellComponent
            errors={data.flowRunErrors}
            flowId={data.flowId}
            flowLuid={data.flow.luid}
            flowStatus={data.status}
            onConnectionsPage={false}
            siteUrls={siteUrls}
            isDownloadAllowed={isDownloadAllowed}
            urlName={data.site && data.site.urlName}
          />
        );
      }
    );
  }

  export function FlowTaskOutputStepsWithIconsCell(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowTaskOutputStepsWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowTaskOutputStepsWithIconsCellComponent flowId={data.flowId} outputSteps={data.flowOutputSteps}/>
        );
      }
    );
  }

  export function FlowTaskStatusWithIconsCell(siteUrls: SiteUrls, flowFailureThresholdForRunPrevention: number): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowTaskStatusWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowTaskStatusWithIconsCellComponent
            flowId={data.flowId}
            siteUrls={siteUrls}
            runFlowTaskInfo={data as RunFlowTaskInfo}
            flowFailureThresholdForRunPrevention={flowFailureThresholdForRunPrevention}
          />
        );
      }
    );
  }
}


export function FlowParametersInOutputCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowParametersInOutputCell(props: BodyCell.Props) {
      const data: FlowOverviewDataGridRow = props.row.data;
      return (
        <FlowParametersCellComponent parameters={data.flowOutputStep.relevantParameters} parameterRuns={data.outputStepMostRecentRun?.relevantParameterRuns}/>
      );
    }
  );
}

const cssPrefix = 'FlowDataGridCells';

const iconClass = cssClass(cssPrefix, 'iconClass', baseBackgroundImgIcon, {
  width: '25px',
  height: '25px',
  margin: `${TabStyles.Spacing.DefaultSpace}px ${TabStyles.Spacing.DefaultSpace}px ${TabStyles.Spacing.DefaultSpace}px 0`,
  display: 'inline-block',
  verticalAlign: 'top',
  flex: '0 0 auto'
});

const iconColorClass = cssClass(cssPrefix, 'iconColor', {
  backgroundColor: TabStyles.Colors.F8,
});

type FlowOverviewOutputStepCellComponentProps = {
  readonly outputName: string;
  readonly outputStep: FlowOutputStepInfo;
};

class FlowOverviewOutputStepCellComponent extends React.PureComponent<FlowOverviewOutputStepCellComponentProps> {

  private getContent = (): Promise<JSX.Element> => {
    return Promise.resolve(<FlowOutputStepCard outputStep={this.props.outputStep}/>);
  };
  public render(): JSX.Element {
    return (
      <Tooltip getContent={this.getContent} position={TooltipPosition.Right}>
        <span>
          <div className={classes(iconClass, iconColorClass, getStepIconClass('output', getConnectionCategory(this.props.outputStep.dataConnection), !!this.props.outputStep.dataRoleId))} />
          <span>{this.props.outputStep.name}</span>
        </span>
      </Tooltip>
    );
  }
}

const suspendedIconResource = 'tb-icon-server-alert-list-suspended';

const suspendedIconClass = cssClass(cssPrefix, 'suspendedIconClass', {
  paddingRight: 4,
  flexShrink: 0
});

const tooltipRoot = cssClass(cssPrefix, 'tooltipRoot', {
  padding: TabStyles.Spacing.DefaultSpace
});

const columnFlexContainer = cssClass(cssPrefix, 'columnFlexContainer', flexColumnStyle);

const suspendedIconWithTextWrapper = cssClass(cssPrefix, 'suspendedIconWithTextWrapper', flexRowStyle, {
  alignItems: 'center'
});

const tooltipLink = cssClass(cssPrefix, 'tooltipLink', {
  paddingTop: TabStyles.Spacing.DefaultSpace
});

const tooltipItem = cssClass(cssPrefix, 'tooltipItem', textEllipsis, {
  paddingTop: TabStyles.Spacing.DefaultSpace,
  paddingBottom: TabStyles.Spacing.DefaultSpace
});

const countClass = cssClass(cssPrefix, 'count', {
  color: CommonStyles.Colors.Grey153,
  flex: 'none',
  marginLeft: CommonStyles.Sizes.DefaultSpace
});

const textEllipsisClass = cssClass(cssPrefix, 'textEllipsis', textEllipsis);

const rightCenterOriginOptions = {
  horizontal: HorizontalOriginOption.right,
  vertical: VerticalOriginOption.centerY
};

type FlowOverviewScheduleCellComponentProps = {
  readonly flow: FlowBaseInfo;
  readonly outputId: string;
  readonly tasks: ReadonlyArray<RunFlowTaskInfo>;
  readonly siteUrls: SiteUrls;
  readonly addScheduleAllowed: boolean;
  readonly isServerOrSiteAdmin: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly onUpdate: () => void;
};

class FlowOverviewScheduleCellComponent extends React.PureComponent<FlowOverviewScheduleCellComponentProps> {

  private getSchedulesTooltipContent = (): JSX.Element => {
    /* eslint-disable jsx-a11y/anchor-has-content */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <div>
        <a href=""/>
        <div className="tb-content-item-popover-title">{Messages.flowTasksCount({FLOW_TASK_COUNT_LOC: Formatters.formatCount(this.props.tasks.length)})}</div>
        <div className={columnFlexContainer}>
          {this.props.tasks.map((task, index) => {
            return (
            <div key={index} className={suspendedIconWithTextWrapper}>
              {task.suspended ?
                <div
                  className={classes(suspendedIconResource, suspendedIconClass)}
                  title={Messages.suspended()}
                />
                : null}
              <div className={tooltipItem}>
                {this.renderScheduleText(index)}
              </div>
            </div>);
          })}
          {this.props.addScheduleAllowed ? <div className={tooltipLink}>{this.renderAddScheduleButton()}</div> : null}
          {areFlowParametersEnabled() ? <div className={tooltipLink}>{this.renderFlowParametersButton()}</div>: null}
        </div>
      </div>
    );
  };

  private addScheduleForOutput = () => {
    openCreateFlowRunTaskDialog(this.props.flow, this.props.outputId).then(this.props.onUpdate);
  };

  private renderScheduleText = (index: number = 0): JSX.Element => {
    return this.props.isServerOrSiteAdmin ? this.renderScheduleTextWithAdminPermissions(index) : this.renderScheduleTextWithoutPermissions(index);
  };

  private renderScheduleTextWithAdminPermissions = (index: number): JSX.Element => {
    return this.props.tasks.length ? this.renderScheduleWithLink(this.props.tasks[index]) : this.renderNoSchedules();
  };

  private renderScheduleTextWithoutPermissions = (index: number): JSX.Element => {
    return this.props.tasks.length ?
      <span title={this.props.tasks[index].schedule.name} className={textEllipsisClass}>{this.props.tasks[index].schedule.name}</span>
      : this.renderNoSchedules();
  };

  private renderNoSchedules = (): JSX.Element => {
    return this.props.addScheduleAllowed ? this.renderAddScheduleButton() : <div>{Messages.flowOverviewNoScheduledTasks()}</div>;
  };

  private renderCount = (): JSX.Element => {
    return (this.props.tasks.length - 1) > 0 ?
      <span className={countClass}>{`+ ${this.props.tasks.length - 1}`}</span>
      : null;
  };

  private renderAddScheduleButton = (): JSX.Element => {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <a onClick={this.addScheduleForOutput}>{Messages.flowOverviewCreateNewTask()}</a>
    );
  };

  private renderFlowParametersButton = (): JSX.Element => {
    return <TextLinkWidget href={this.props.siteUrls.flowScheduledTasksUrl(this.props.flow.id)} testId={'scheduled-tasks-flow-parameters-link'}>{Messages.flowViewScheduledTaskDetails()}</TextLinkWidget>;
  };

  private renderScheduleWithLink = (task: RunFlowTaskInfo): JSX.Element => {
    return (
      <a href={this.props.siteUrls.scheduleFlowsUrl(task.schedule.id)} title={task.schedule.name}>
        {task.schedule.name}
      </a>
    );
  };

  public render(): JSX.Element {
    const suspendedTasks = this.props.tasks.filter((task) => {
      return task.suspended;
    });
    return (
      <div className={suspendedIconWithTextWrapper}>
        {this.props.tasks.length === 1 && this.props.tasks[0].suspended ?
          <FlowSuspendedIconWithTooltip
            flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
            flowId={this.props.flow.id}
            siteUrls={this.props.siteUrls}
          />
          : null}
        {this.props.tasks.length > 1 && suspendedTasks.length > 0 ?
          <FlowSuspendedIconWithTooltip
            flowId={this.props.flow.id}
            siteUrls={this.props.siteUrls}
            suspendedTasksCount={suspendedTasks.length}
          />
          : null}
        {this.props.tasks.length > 1 ?
          <TooltipAnchor
            width={264} // 300 - (36 padding) = renders at 300
            tooltipContent={this.getSchedulesTooltipContent}
            delay={0}
            anchorOrigin={rightCenterOriginOptions}
            title={Messages.flowSchedulesTooltipTitle()}
          >
            {this.renderScheduleText()}
            {this.renderCount()}
          </TooltipAnchor> :
        this.renderScheduleText()}
      </div>
    );
  }
}

const outputNameCellContainer = cssClass(cssPrefix, 'outputNameCell', flexRowStyle);
const outputNameCellIcon = cssClass(cssPrefix, 'outputNameCellIcon', {
  flex: '0 0 auto'
});
const outputNameCellText = cssClass(cssPrefix, 'outputNameCellText', textEllipsis);
const outputNameCellTextDisabled = cssClass(cssPrefix, 'outputNameCellTextDisabled', textEllipsis, {
  color: TabStyles.Colors.F7
});

type FlowOverviewOutputNameCellComponentProps = {
  readonly siteUrls: SiteUrls;
  readonly outputStep: FlowOutputStepInfo;
  readonly outputStepMostRecentRun: FlowOutputStepRunListItemInfo;
};

type FlowOverviewOutputNameCellComponentState = {
  readonly userHasPermission: boolean;
};

class FlowOverviewOutputNameCellComponent extends React.PureComponent<FlowOverviewOutputNameCellComponentProps, FlowOverviewOutputNameCellComponentState> {

  constructor(props: FlowOverviewOutputNameCellComponentProps) {
    super(props);
    this.state = {
      userHasPermission: false
    };
    this.checkUserPermissionForDataSource();
  }

  public componentDidUpdate(prevProps: FlowOverviewOutputNameCellComponentProps) {
    if (prevProps.outputStep.datasourceId !== this.props.outputStep.datasourceId) {
      this.checkUserPermissionForDataSource();
    }
  }

  private checkUserPermissionForDataSource = () => {
    if (this.props.outputStep.datasourceId) {
      DataSources.getById(this.props.outputStep.datasourceId).then(result => {
        this.setState({
          userHasPermission: !!result.id
        });
      });
    }
  };

  private renderCellText = () => {
    const latestRun = this.props.outputStepMostRecentRun;
    const outputName = this.props.outputStep.datasourceName;
    const datasourceId = this.props.outputStep.datasourceId;
    const dataRoleId = this.props.outputStep.dataRoleId;

    // (mechen) During site export/import, flow run history will be lost, so latestRun might be undefined.
    // But if the output datasource exist and is valid, display the datasource link.
    if (this.props.outputStep && datasourceId && this.state.userHasPermission) {
      return <a href={this.props.siteUrls.datasourceUrl(datasourceId)} className={outputNameCellText} title={outputName}>{outputName}</a>;
    }
    else if (this.props.outputStep && dataRoleId) {
      return <a href={this.props.siteUrls.dataRoleUrl(dataRoleId)} className={outputNameCellText} title={outputName}>{outputName}</a>;
    }
    else if (latestRun) {
      return <div className={outputNameCellText} title={outputName}>{outputName}</div>;
    } else {
      const cellText = outputName + ' ' + Messages.flowOverviewNotYetPublished();
      return <div className={outputNameCellTextDisabled} title={cellText}>{cellText}</div>;
    }
  };

  public render(): JSX.Element {
    return (
      <div className={outputNameCellContainer}>
        <div className={classes(outputNameCellIcon, getOutputStepIconFromConnectionCategory(getConnectionCategory(this.props.outputStep.dataConnection)))}/>
        {this.renderCellText()}
      </div>
    );
  }
}

const outputLinkClass = cssClass(cssPrefix, 'outputLink', {
  fontSize: CommonStyles.Sizes.LargeFontSize,
  ...textEllipsis
});

const datasourcesCell = cssClass(cssPrefix, 'datasourcesCell', {
  display: 'flex',
  alignItems: 'end',
  columnGap: 5,
  minWidth: 200,
});

const popoverMaxWidth = cssClass(cssPrefix, 'popoverMaxWidth', {
  maxWidth: 300
});

interface FlowDatasourcesForOutputCellComponentProps {
  siteUrls: SiteUrls;
  outputStep: FlowOutputStepInfo;
  wasRanBefore: boolean;
}

function amendConnectedDatasourcesFromDataConnection(outputStep: FlowOutputStepInfo): DatasourceInfoForFlowOutput[] {
  if(!outputStep.dataConnection){
    return [];
  }
  return [{
    connectionDetails: outputStep.dataConnection.connectionDetails,
    connectionType: outputStep.dataConnection.connectionType,
    id: outputStep.datasourceId,
    name: outputStep.datasourceName ? outputStep.datasourceName : ''
  }];
}


function FlowDatasourcesForOutputCellComponent(props: FlowDatasourcesForOutputCellComponentProps): JSX.Element {
  const [permissionsMap, setPermissionsMap] = React.useState<{[id: string]: boolean}>({});

  let datasources = props.outputStep.connectedDatasources;

  // when connectedDatasources is empty, we should repopulate it with data from connectionDetails
  if(datasources.length === 0){
    datasources = amendConnectedDatasourcesFromDataConnection(props.outputStep);
  }
  React.useEffect(() => {
    if (!props.wasRanBefore || Object.keys(permissionsMap).length > 0) {
      return;
    }
    const map: {[id: string]: boolean} = {};
    const datasourceIds = new Set((value: string) => value);
    datasources.forEach(datasource => {
      if (datasource.id && datasource.connectionDetails.type !== 'file') {
        datasourceIds.add(datasource.id);
      }
    });
    const promises: Promise<any>[] = [];
    datasourceIds.toArray().forEach(id => {
      promises.push(DataSources.getById(id).then(result => {
        map[id] = !!result.id;
      }).catch(_e => {}));
    });
    Promise.all(promises).finally(() => {
      setPermissionsMap(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderDatasourceLink(datasource: DatasourceInfoForFlowOutput) {
    const connectionType = datasource?.connectionDetails?.type;
    if (permissionsMap[datasource.id]) {
      // Datasource with permission to view
      return (
        <TextLinkWidget
          href={props.siteUrls.datasourceUrl(datasource.id)}
          className={outputLinkClass}
          title={datasource.name}
          testId={'flow-output-datasource-link-' + datasource.id}
        >
          {datasource.name}
        </TextLinkWidget>
      );
    } else if (permissionsMap[datasource.id] === false) {
      // Datasource without permission to view
      return <div className={outputLinkClass} style={{fontStyle: 'italic'}}>{Messages.permissionsRequiredLabel()}</div>;
    } else if (!(connectionType === 'tableauserversite' || connectionType === 'tableauserver')) {
      // Non-datasource type
      return <div className={outputLinkClass} title={datasource.name}>{datasource.name}</div>;
    } else {
      // Show nothing if permissions are not loaded yet
      return;
    }
  }

  function renderDatasource(datasource: DatasourceInfoForFlowOutput): JSX.Element {
    return (
      <div className={classes(flowTaskOutputIconClass, outputTooltipItem)}>
        <div
          className={iconClass}
          style={getOutputIconStyle(connectionDetailsTypeToConnectionCategory(datasource.connectionDetails.type), false)}
        />
        {renderDatasourceLink(datasource)}
      </div>
    );
  }

  function renderOutputText() {
    const datasourceId = props.outputStep.datasourceId;
    const outputName = props.outputStep.datasourceName;
    const dataRoleId = props.outputStep.dataRoleId;
    if (permissionsMap[datasourceId]) {
      return (
        <TextLinkWidget
          href={props.siteUrls.datasourceUrl(datasourceId)}
          className={outputLinkClass}
          title={outputName}
          testId={'flow-output-datasource-link-' + datasourceId}
        >
          {outputName}
        </TextLinkWidget>
      );
    } else if (dataRoleId) {
      return (
        <TextLinkWidget
          href={props.siteUrls.dataRoleUrl(dataRoleId)}
          className={outputLinkClass}
          title={outputName}
          testId={'flow-output-datarole-link-' + dataRoleId}
        >
          {outputName}
        </TextLinkWidget>
      );
    } else if (props.wasRanBefore) {
      return <div className={outputLinkClass} title={outputName}>{outputName}</div>;
    } else if (props.outputStep.relevantParameters.length > 0) {
      return <div className={classes(outputLinkClass, outputNameCellTextDisabled)}>{Messages.flowOutputShownAfterFirstRun() }</div>;
    } else {
      const cellText = outputName + ' ' + Messages.flowOverviewNotYetPublished();
      return <div className={classes(outputLinkClass, outputNameCellTextDisabled)} title={cellText}>{cellText}</div>;
    }
  }

  function renderOutput(): JSX.Element {
    return (
      <div className={classes(flowTaskOutputIconClass, outputTooltipItem)}>
        <div
          className={iconClass}
          style={getOutputIconStyle(connectionDetailsTypeToConnectionCategory(props.outputStep?.dataConnection?.connectionDetails?.type), false)}
        />
        {renderOutputText()}
      </div>
    );
  }

  function renderFirstDatasource(): JSX.Element | null {
    return props.outputStep?.relevantParameters.length > 0 && datasources?.length > 0 ? renderDatasource(datasources[0]) : renderOutput();
  }

  function popoverContent() {
    return (
      <div className={popoverMaxWidth}>
        <div className={columnFlexContainer}>
          {datasources.map(datasource => {
            return <div key={datasource.id} className={outputTooltipItem}>{renderDatasource(datasource)}</div>;
          })}
        </div>
      </div>
    );
  }

  function cellContent() {
    return (
      <div className={datasourcesCell}>
        {renderFirstDatasource()}
        {`+${datasources.length - 1}`}
      </div>
    );
  }

  return datasources?.length > 1 ? (
    <CellWithPopover
      cellContent={cellContent()}
      popoverContent={popoverContent()}
      title={props.outputStep.name}
    />
  ) : renderFirstDatasource();
}

type FlowOverviewStatusCellComponentProps = {
  readonly outputStep: FlowOutputStepInfo;
  readonly outputStepMostRecentRun: FlowOutputStepRunListItemInfo;
  readonly flowId: string;
  readonly siteUrls: SiteUrls;
  readonly schedulingFlowsEnabled: boolean;
};

class FlowOverviewStatusCellComponent extends React.PureComponent<FlowOverviewStatusCellComponentProps> {

  private renderStatusMessageAsTextOrLink(statusMessage: string) {
    return this.props.schedulingFlowsEnabled ?
      <a href={this.props.siteUrls.flowRunHistoryUrl(this.props.flowId)}>
        {statusMessage}
      </a> :
      <span>{statusMessage}</span>;
  }

  private renderStatus = () => {
    if (!this.props.outputStepMostRecentRun) {
      return <div>{Messages.flowOverviewNeverRun()}</div>;
    } else if (this.props.outputStepMostRecentRun.status === FlowOutputStepRunStatus.pending) {
      return this.renderStatusMessageAsTextOrLink(Messages.flowOverviewPendingRun());
    } else if (this.props.outputStepMostRecentRun.status === FlowOutputStepRunStatus.inProgress) {
      return this.renderStatusMessageAsTextOrLink(Messages.flowOverviewRunning());
    } else {
      // render completed
      const statusMessage = `${localizeFlowRunHistoryStatus(this.props.outputStepMostRecentRun.status)}: ${Formatters.formatMediumDateTimeWithoutSeconds(this.props.outputStepMostRecentRun.completedAt)}`;
      return this.renderStatusMessageAsTextOrLink(statusMessage);
    }
  };

  private renderIcon = () => {
    return <div className={classes(getFlowOutputStepRunIconClass(this.props.outputStepMostRecentRun.status))}/>;
  };

  public render(): JSX.Element {
    return (
      this.props.outputStepMostRecentRun ?
        <div className={textEllipsisClass}>{this.renderIcon()}{this.renderStatus()}</div>
      : <div>{Messages.flowOverviewNeverRun()}</div>
    );
  }
}

const errorItemClass = cssClass(cssPrefix, 'errorItem', {
  padding: `${TabStyles.Spacing.DefaultSpace}px 0px`
});
const errorsCellText = cssClass(cssPrefix, 'errorsCellText', textEllipsis, {
  color: CommonStyles.Colors.HyperlinkBlue,
  textDecoration: 'underline',
});
const hyperlinkSpacingClass = cssClass(cssPrefix, 'hyperlinkSpacing', {
  padding: `${TabStyles.Spacing.SmallSpace}px 0px`
});

export type FlowOverviewErrorsCellComponentProps = {
  readonly errors: ReadonlyArray<FlowRunError>;
  readonly flowId: string;
  readonly flowLuid?: string;
  readonly flowStatus?: RunFlowTaskStatus;
  readonly onConnectionsPage: boolean;
  readonly isDownloadAllowed: boolean;
  readonly siteUrls: SiteUrls;
  readonly urlName?: string; // used to ensure we get the right siteUrl for a row
  readonly isLinkedTask?: boolean;
};

export class FlowOverviewErrorsCellComponent extends React.PureComponent<FlowOverviewErrorsCellComponentProps> {

  private siteUrlsForRow: SiteUrls;
  private errors: ReadonlyArray<FlowRunError>;

  constructor(props: FlowOverviewErrorsCellComponentProps) {
    super(props);
    this.siteUrlsForRow = this.props.urlName != null ? this.props.siteUrls.forSiteName(this.props.urlName) : this.props.siteUrls;

    // DEFECT 1406862
    // If the Backgrounder crashes or terminated while running a Flow, the flow fails without any error
    // message attached to it. Below ensures that a generic error message gets displayed in such case.
    this.errors = this.props.flowStatus === RunFlowTaskStatus.Failed && this.props.errors.length === 0
      ? [{ flowRunErrorCode: 288005 }]  // FlowRunVizportalAuthenticationError
      : this.props.errors;
  }

  private downloadFlow = () => {
    if (this.props.urlName != null) {
      SiteSwitchService.switchIfNeededNoFeedback(this.props.urlName)
      .then(() => DownloadActions.downloadFlow({id: this.props.flowId}));
    } else {
      DownloadActions.downloadFlow({id: this.props.flowId});
    }
  };

  private getContent = () => {
    /* eslint-disable jsx-a11y/anchor-is-valid */
    /* eslint-disable jsx-a11y/anchor-has-content */
    return (
      <div>
        <a href=""/>
        <div className={'tb-content-item-popover-title'}>{Messages.flowErrorsCount({COUNT: this.errors.length, COUNT_LOC: Formatters.formatCount(this.errors.length)})}</div>
        <div>{this.errors.map(this.renderError)}</div>
        {!this.props.isLinkedTask ?
          <div className={columnFlexContainer}>
            {!this.props.onConnectionsPage ? this.renderConnectionsLink() : null}
            {this.props.isDownloadAllowed ? this.renderOpenFlowLink() : null}
          </div>
          : null}
      </div>
    );
  };

  private renderOpenFlowLink = () => {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return needsFixInPrepDesktop(this.errors) ? <a className={hyperlinkSpacingClass} href={this.siteUrlsForRow.flowEditorFlowPath({ flowLuid: this.props.flowLuid })} target="_blank" rel="noopener noreferrer">{Messages.flowErrorsOpenFlow()}</a> : null;
  };

  private renderConnectionsLink = () => {
    const inputConnectionError = this.errors.find(
      error => isConnectionRelatedError(error)
    );
    return inputConnectionError ? <a href={this.siteUrlsForRow.flowConnectionsUrl(this.props.flowId)} className={hyperlinkSpacingClass}>{Messages.flowOverviewGoToConnections()}</a> : null;
  };

  private renderError = (error: FlowRunError, index: number) => {
    return <div key={index} className={errorItemClass}>{flowRunErrorMessageWithLinks(error)}</div>;
  };

  private renderCellText = () => {
    return <div className={errorsCellText}>{Messages.flowErrorsCount({COUNT: this.errors.length, COUNT_LOC: Formatters.formatCount(this.errors.length)})}</div>;
  };

  private renderCellWithErrors = () => {
    return (
      <TooltipAnchor
        tooltipContent={this.getContent}
        width={450}
        delay={0}
        anchorOrigin={rightCenterOriginOptions}
        title={Messages.errorsTooltipTitle()}
      >
        {this.renderCellText()}
      </TooltipAnchor>
    );
  };

  public render(): JSX.Element {
    return this.errors != null && this.errors.length ?
      this.renderCellWithErrors() :
      null;
  }
}

const statusCell = cssClass(cssPrefix, 'statusCell', {
  display: 'flex',
});

const statusIcon = cssClass(cssPrefix, 'statusIcon', {
  flexShrink: 0,
});

const statusText = cssClass(cssPrefix, 'statusText', textEllipsis);

export type FlowTaskStatusWithIconsCellComponentProps = {
  readonly flowId: string;
  readonly siteUrls: SiteUrls;
  readonly runFlowTaskInfo: RunFlowTaskInfo;
  readonly flowFailureThresholdForRunPrevention: number; // number of failures before auto-suspend (-1 means disabled, shouldnt hit that case, but handle gracefully)
};

export class FlowTaskStatusWithIconsCellComponent extends React.PureComponent<FlowTaskStatusWithIconsCellComponentProps> {

  private renderCellText = () => {
    const localizedStatus = localizeRunFlowTaskStatus(this.props.runFlowTaskInfo.status);
    return <span className={statusText} title={localizedStatus}>{localizedStatus}</span>;
  };

  private renderIcon = () => {
    const runFlowTaskStatusIcon = getRunFlowTaskStatusIconClass(this.props.runFlowTaskInfo.status);
    return <span className={classes(runFlowTaskStatusIcon, statusIcon)} />;
  };

  public render(): JSX.Element {
    return (
      <div className={statusCell}>
        {this.props.runFlowTaskInfo.suspended ?
        <FlowSuspendedIconWithTooltip
          flowId={this.props.flowId}
          siteUrls={this.props.siteUrls}
          flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
          urlName={this.props.siteUrls && this.props.runFlowTaskInfo ? this.props.runFlowTaskInfo.site.urlName : null}
        /> :
        this.renderIcon()}
        {this.renderCellText()}
      </div>
    );
  }
}

const flowTaskOutputIconClass = cssClass(cssPrefix, 'flowTaskOutputIcon', flexRowStyle, {
  alignItems: 'center'
});

const outputTooltipItem = cssClass(cssPrefix, 'outputTooltipItem', textEllipsis);

const outputLocationClass = cssClass(cssPrefix, 'outputLocation', {
  paddingLeft: TabStyles.Spacing.DefaultSpace,
  color: CommonStyles.Colors.Grey153
});

export type FlowTaskOutputStepsWithIconsCellComponentProps = {
  readonly flowId: string;
  readonly outputSteps: FlowOutputStepInfo[];
};

export class FlowTaskOutputStepsWithIconsCellComponent extends React.PureComponent<FlowTaskOutputStepsWithIconsCellComponentProps> {
  private getContent = (): Promise<JSX.Element> => {
    return Promise.resolve(
      <div className={tooltipRoot}>
        <div className="tb-content-item-popover-title">{Messages.outputStepsCount({COUNT: this.props.outputSteps.length, COUNT_LOC: Formatters.formatCount(this.props.outputSteps.length)})}</div>
        <div className={columnFlexContainer}>
          {this.props.outputSteps.map((outputStep) => {
            return <div key={outputStep.id} className={outputTooltipItem}>{this.renderSingleOutput(outputStep)}</div>;
          })}
        </div>
      </div>
    );
  };

  private renderSingleOutput = (output: FlowOutputStepInfo): JSX.Element => {
    const filePath: string = output.dataConnection && filePathDisplayStringWithoutBaseName((output.dataConnection.connectionDetails as FileConnectionDetail).fileName);
    const outputLocationName: string = filePath ? filePath : getConnectionText(output.dataConnection);
    return (
      <div className={classes(flowTaskOutputIconClass, outputTooltipItem)}>
        <div className={classes(iconClass, iconColorClass, getStepIconClass('output', getConnectionCategory(output.dataConnection), !!output.dataRoleId))}/>
        <div className={outputTooltipItem}>
          <span title={output.name + ' ' + Messages.flowOutputLocationDetail({LOCATION: outputLocationName})}>
            {output.name}
          </span>
          <span className={outputLocationClass}>
            {Messages.flowOutputLocationDetail({LOCATION: outputLocationName})}
          </span>
        </div>
      </div>
    );
  };

  private renderFirstOutput = (outputSteps: ReadonlyArray<FlowOutputStepInfo> | null): JSX.Element | null => {
    if (outputSteps && outputSteps.length > 0) {
      return this.renderSingleOutput(outputSteps[0]);
    } else {
      return null;
    }
  };

  public render(): JSX.Element {
    if (this.props.outputSteps && this.props.outputSteps.length > 1) {
      return (
        <CellWithCountAndToolTip count={`+${this.props.outputSteps.length - 1}`} getPopoverContent={this.getContent}>
          {this.renderFirstOutput(this.props.outputSteps)}
        </CellWithCountAndToolTip>
      );
    } else {
      return this.renderFirstOutput(this.props.outputSteps);
    }
  }

}

interface FlowParameterNameAndValueProps {
  name: string;
  id: string;
  value: string;
  type: FlowParameterType;
}

export function FlowParameterNameAndValue(props: FlowParameterNameAndValueProps): JSX.Element {
  let dateValue: string;
  if (props.type === 'date') {
    dateValue = new Date(Number(props.value)).toDateString();
  } else if (props.type === 'datetime') {
    dateValue = new Date(Number(props.value)).toString();
  }
  return (
    <div>
      {`<${props.name}:`}
      <span className={boldWrapper}>{dateValue ? dateValue : props.value}</span>
      {'>'}
    </div>
  );
}

export interface FlowParametersCellComponentProps {
  parameters?: FlowParameter[];
  parameterRuns?: FlowParameterRun[];
}

const infoIcon = imgPath('info_normal.svg');

const descriptionIcon = style({
  height: 16,
  marginLeft: 6,
  verticalAlign: 'text-top',
  outlineStyle: 'none'
});

const parameterItemClass = style({
  margin: '4px 0px',
  display: 'flex',
});

const parameterContentWrapper = style({
  marginTop: 8,
});

const parameterCellWrapper = style({
  display: 'flex',
});

const boldWrapper = style({
  fontWeight: 500,
});

const countSpacing = style({
  paddingLeft: 3
});

export function FlowParametersCellComponent(props: FlowParametersCellComponentProps): JSX.Element {

  const [parameterMap, setParameterMap] = React.useState<{[key: string]: string}>({});

  React.useEffect(() => {
    const map: {[key: string]: string} = {};
    props.parameterRuns && props.parameterRuns.forEach(run => {
      map[run.parameterId] = run.overrideValue;
    });
    setParameterMap(map);
  }, [props.parameterRuns]);

  if (props.parameters === undefined && props.parameterRuns === undefined) {
    return null;
  }

  function renderDescription(description: string): JSX.Element {
    return (
      <Tooltip getContent={() => <div>{description}</div>}>
        <img className={descriptionIcon} src={infoIcon} alt={Messages.flowParameterDescription()}/>
      </Tooltip>
    );
  }



  function renderParameters() {
    const parameters: (FlowParameter | FlowParameterRun)[] = props.parameterRuns || props.parameters;
    return parameters.map(parameter => {
      // Check if this parameter run only case
      if (isFlowParameterRun(parameter)) {
        return (
          <div key={parameter.parameterId} className={parameterItemClass}>
            <FlowParameterNameAndValue
              name={parameter.name}
              id={parameter.parameterId}
              value={parameter.overrideValue}
              type={parameter.domain.type}
            />
            {parameter.description ? renderDescription(parameter.description) : null}
          </div>
        );
      } else {
        return (
          <div key={parameter.id} className={parameterItemClass}>
            <FlowParameterNameAndValue
              name={parameter.name}
              id={parameter.id}
              value={parameterMap[parameter.id] || parameter.value}
              type={parameter.domain.type}
            />
            {parameter.description ? renderDescription(parameter.description) : null}
          </div>
        );
      }
    });
  }

  const getContent = () => {
    /* eslint-disable jsx-a11y/anchor-is-valid */
    /* eslint-disable jsx-a11y/anchor-has-content */
    const count = props.parameters !== undefined ? props.parameters.length : props.parameterRuns.length;
    return (
      <div className={parameterContentWrapper}>
        <a href=""/>
        <div className={'tb-content-item-popover-title'}>
          {Messages.flowParametersUsedInFlowCount({USED_PARAMETER_COUNT: count, USED_PARAMETER_COUNT_LOC: Formatters.formatCount(count)})}
        </div>
        {renderParameters()}
      </div>
    );
  };

  function cellText() {
    let firstParamProps: FlowParameterNameAndValueProps;
    let count: number;
    if (props.parameters === undefined && props.parameterRuns.length === 0) {
      return;
    }
    if (props.parameters === undefined && props.parameterRuns.length > 0 && isFlowParameterRun(props.parameterRuns[0])) {
      const firstParam = props.parameterRuns[0];
      firstParamProps = {
        name: firstParam.name,
        id: firstParam.parameterId,
        value: firstParam.overrideValue,
        type: firstParam.domain.type,
      };
      count = props.parameterRuns.length;
    } else {
      const firstParam = props.parameters.length > 0 ? props.parameters[0] : null;
      firstParamProps = firstParam && {
        name: firstParam.name,
        id: firstParam.id,
        value: parameterMap[firstParam.id] || firstParam.value,
        type: firstParam.domain.type
      };
      count = props.parameters.length;
    }
    if (!firstParamProps) {
      return;
    }
    return (
      <div className={parameterCellWrapper}>
        <FlowParameterNameAndValue {...firstParamProps} />
        {count > 1 ? <div className={classes(boldWrapper, countSpacing)}>{`+${count - 1}`}</div> : null}
      </div>
    );
  }

  return (
    <TooltipAnchor
      tooltipContent={getContent}
      width={400}
      delay={0}
      anchorOrigin={rightCenterOriginOptions}
      title={Messages.flowParametersHeader()}
    >
      {cellText()}
    </TooltipAnchor>
  );
}
